/**
 * 全カード共通の枠 + 左右ハンドル + 削除ボタン + 外部リンクボタン。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11.5
 */
import type { CampaignElementType } from '@goenhub/shared';
import { Handle, Position } from '@xyflow/react';
import { Check, Copy, ExternalLink, MessageCircle, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';


import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';

interface Props {
  type: CampaignElementType;
  /** カードのアクセント色クラス（type ごとに変える） */
  accentClass: string;
  icon: ReactNode;
  title: string;
  /** 表示中のリソース名（ない時は「未設定」表記など） */
  subtitle?: string;
  externalUrl?: string;
  /**
   * LIFF 用 URL（form カード専用、Phase 24f）。指定時は「LINE 用」ボタンを追加表示する。
   * https://liff.line.me/{LIFF_ID}?formId=...&campaignId=...
   */
  liffUrl?: string;
  /**
   * referral カード専用：発行されたタグ付き URL。
   * 指定時は「URL コピー」ボタンを追加表示する。
   */
  referralUrl?: string;
  /** カードの本体に追加描画する要素（任意） */
  children?: ReactNode;
  /** カード詳細を開く */
  onOpen?: () => void;
  /** カード削除 */
  onDelete?: () => void;
  /** ハンドル（接続線の起点・終点）を有効にするか。note は無効。*/
  enableHandles?: boolean;
  /** 到達者数（plan adaptive-crown §機能 6） */
  reachCount?: number | null;
  /** reach バッジのラベル (例: "閲覧" "送信" "回答") */
  reachLabel?: string;
}

export function BaseCardNode({
  type,
  accentClass,
  icon,
  title,
  subtitle,
  externalUrl,
  liffUrl,
  referralUrl,
  children,
  onOpen,
  onDelete,
  enableHandles = true,
  reachCount,
  reachLabel,
}: Props) {
  // 「LINE 用」ボタン押下後の短時間（〜1.2 秒）「コピー済」表示
  const [liffCopied, setLiffCopied] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);

  const handleCopyLiff = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!liffUrl) return;
    try {
      await navigator.clipboard.writeText(liffUrl);
      setLiffCopied(true);
      toast.success('LIFF URL をコピーしました');
      window.setTimeout(() => setLiffCopied(false), 1200);
    } catch (err) {
      toast.error(
        `コピーに失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const handleCopyReferral = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setReferralCopied(true);
      toast.success('タグ付き URL をコピーしました');
      window.setTimeout(() => setReferralCopied(false), 1200);
    } catch (err) {
      toast.error(
        `コピーに失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <div
      className={`relative rounded-md border-2 bg-[var(--bg-elevated)] shadow-sm min-w-[180px] max-w-[260px] ${accentClass}`}
    >
      {enableHandles && (
        <>
          <Handle
            type="target"
            position={Position.Left}
            id="left"
            className="!size-3 !bg-[var(--text-muted)] !border-2 !border-[var(--bg-elevated)]"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="right"
            className="!size-3 !bg-[var(--text-muted)] !border-2 !border-[var(--bg-elevated)]"
          />
        </>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left p-2.5 cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-[12px] font-semibold flex-1 truncate">{title}</span>
          <code className="text-[9px] font-mono text-[var(--text-muted)]">{type}</code>
        </div>
        {subtitle && (
          <div className="text-[11px] text-[var(--text-secondary)] truncate mt-1">
            {subtitle}
          </div>
        )}
        {typeof reachCount === 'number' && reachCount > 0 && (
          <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[var(--accent-subtle)] text-[var(--accent)]">
            <span className="text-[10px] font-semibold">{reachCount}</span>
            <span className="text-[10px]">{reachLabel ?? '到達'}</span>
          </div>
        )}
        {children}
      </button>
      <div className="flex items-center gap-1 px-2 pb-1.5">
        {externalUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="!h-6 !text-[10px] !px-1.5"
            onClick={(e) => {
              e.stopPropagation();
              window.open(externalUrl, '_blank', 'noopener');
            }}
            title="外部リンクを開く（通常 URL）"
          >
            <ExternalLink className="size-3" />
            開く
          </Button>
        )}
        {liffUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="!h-6 !text-[10px] !px-1.5 !text-[#06c755] hover:!bg-[#06c755]/10"
            onClick={handleCopyLiff}
            title="LIFF URL をクリップボードにコピー（LINE 配信メッセージにペーストして使用）"
          >
            {liffCopied ? (
              <Check className="size-3" />
            ) : (
              <MessageCircle className="size-3" />
            )}
            {liffCopied ? 'コピー済' : 'LINE 用'}
          </Button>
        )}
        {referralUrl && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="!h-6 !w-6"
            onClick={handleCopyReferral}
            title={`タグ付き URL をコピー: ${referralUrl}`}
          >
            {referralCopied ? (
              <Check className="size-3 text-[var(--accent)]" />
            ) : (
              <Copy className="size-3" />
            )}
          </Button>
        )}
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="!h-6 !text-[10px] !px-1.5 ml-auto"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="カードを削除"
          >
            <Trash2 className="size-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
